<template>
  <div class="postadmin-timeline-page">
    <div class="timeline-toolbar">
      <div class="timeline-toolbar-title">
        <h4 class="timeline-main-title mb-0">{{ tt("twitchPostAdmin.timelineUsers", "Timeline de usuarios") }}</h4>
        <div class="timeline-toolbar-meta">
          <span class="timeline-month-label">{{ timelineMonthLabel }}</span>
          <div class="timeline-status-summary">
            <span class="timeline-stats">{{ tt("twitchPostAdmin.users", "Usuarios") }}: {{ timelineUsers.length }} · {{ tt("twitchPostAdmin.total", "Total") }}: {{ visibleTotalEvents }}</span>
            <span class="timeline-status-pill ready">{{ tt("twitchPostAdmin.scheduled", "Programadas") }}: {{ totalReadyToPost }}</span>
            <span class="timeline-status-pill posted">{{ tt("twitchPostAdmin.posted", "Publicados") }}: {{ totalPosted }}</span>
          </div>
        </div>
      </div>
      <div class="timeline-toolbar-actions">
        <button type="button" class="timeline-nav-btn" @click="shiftTimeline(-1)">
          <i class="ti-angle-left"></i>
        </button>
        <button type="button" class="timeline-nav-btn" @click="shiftTimeline(1)">
          <i class="ti-angle-right"></i>
        </button>
      </div>
    </div>

    <div class="timeline-user-filter">
      <form class="timeline-user-filter-form" @submit.prevent="applyUsernameFilter">
        <div class="timeline-user-filter-input-wrap">
          <i class="ti-search"></i>
          <input
            v-model="usernameFilterInput"
            type="text"
            class="timeline-user-filter-input"
            :placeholder="tt('twitchPostAdmin.usernameFilterPlaceholder', 'Buscar nombre de usuario')"
            @focus="showUsernameSuggestions = true"
            @input="handleUsernameFilterInput"
            @blur="hideUsernameSuggestions"
          />
          <div v-if="showUsernameSuggestions && usernameSuggestions.length > 0" class="timeline-user-suggestions">
            <button
              v-for="user in usernameSuggestions"
              :key="`timeline-user-suggestion-${user.user_id}`"
              type="button"
              class="timeline-user-suggestion"
              @mousedown.prevent="selectUsernameSuggestion(user)"
            >
              <span class="timeline-user-suggestion-name">{{ getTimelineUserName(user) }}</span>
              <small>{{ user.use_timezone || "UTC" }}</small>
            </button>
          </div>
        </div>
        <button
          type="submit"
          class="timeline-user-filter-add"
          :disabled="!usernameFilterInput.trim()"
        >
          {{ tt("twitchPostAdmin.addUsernameFilter", "Agregar") }}
        </button>
        <div class="timeline-user-scope-toggle">
          <button
            type="button"
            class="timeline-user-scope-btn"
            :class="{ active: timelineUserScope === 'mine' }"
            @click="setTimelineUserScope('mine')"
          >
            {{ tt("twitchPostAdmin.showMyUsers", "Mostrar mis usuarios") }}
          </button>
          <button
            type="button"
            class="timeline-user-scope-btn"
            :class="{ active: timelineUserScope === 'all' }"
            @click="setTimelineUserScope('all')"
          >
            {{ tt("twitchPostAdmin.showAllUsers", "Mostrar todos los usuarios") }}
          </button>
        </div>
      </form>
    </div>

    <div ref="timelineSizer" class="timeline-sizer"></div>

    <div v-if="loading" class="timeline-state text-center p-4">
      <i class="ti-reload rotating"></i>
      <p class="mb-0 mt-2">{{ tt("common.loading", "Cargando...") }}</p>
    </div>

    <div v-else-if="errorMessage" class="alert alert-danger">
      {{ errorMessage }}
    </div>

    <div v-else-if="users.length === 0" class="timeline-state text-center p-4">
      <p class="mb-0 text-muted">{{ tt("twitchPostAdmin.noUsersTimeline", "No hay usuarios habilitados para timeline.") }}</p>
    </div>

    <div v-else-if="timelineUsers.length === 0" class="timeline-state text-center p-4">
      <p class="mb-0 text-muted">{{ tt("twitchPostAdmin.noFilteredUsersTimeline", "No hay timelines para los usuarios filtrados.") }}</p>
    </div>

    <div v-else class="timeline-users">
      <div v-for="user in timelineUsers" :key="user.user_id" class="timeline-user-card">
        <div class="timeline-user-header">
          <div>
            <div class="timeline-user-name-row">
              <h5 class="timeline-user-name">{{ user.use_name || user.user_id }}</h5>
              <button
                v-if="canImpersonateUsers() && user.user_info_id"
                type="button"
                class="btn btn-sm timeline-impersonate-btn"
                :disabled="isImpersonatingRow(user)"
                :title="tt('usersList.loginAsUser', 'Entrar como usuario')"
                @click.stop="impersonateTimelineUser(user)"
              >
                <i class="ti-key"></i>
              </button>
              <button
                v-if="canImpersonateUsers() && user.user_info_id"
                type="button"
                class="btn btn-sm timeline-assign-btn"
                :class="{ assigned: isTimelineUserAssigned(user) }"
                :title="tt('twitchPostAdmin.assignAdmin', 'Assign')"
                @click.stop="openAssignAdminModal(user)"
              >
                {{ tt("twitchPostAdmin.assignAdmin", "Assign") }}
              </button>
              <span v-if="isTimelineUserAssigned(user)" class="timeline-assigned-admin-pill">
                {{ getAssignedAdminName(user) }}
              </span>
            </div>
            <small class="text-muted">{{ user.use_timezone || "UTC" }}</small>
          </div>
          <div class="timeline-user-status-summary">
            <span class="timeline-status-pill ready">{{ tt("twitchPostAdmin.scheduled", "Programadas") }}: {{ user.readyToPostCount || 0 }}</span>
            <span class="timeline-status-pill posted">{{ tt("twitchPostAdmin.posted", "Publicados") }}: {{ user.postedCount || 0 }}</span>
          </div>
        </div>

        <div class="timeline-days">
          <div
            v-for="day in timelineDays"
            :key="`${user.user_id}-${day.isoDate}`"
            class="timeline-day-card"
            :class="{
              'has-events': getEventsForDay(user, day.isoDate).length > 0,
              'is-today': day.isToday,
            }"
          >
            <div class="timeline-day-name">{{ day.weekdayLabel }}</div>
            <span v-if="day.isToday" class="timeline-today-badge">{{ tt("common.today", "Hoy") }}</span>
            <div class="timeline-day-number">{{ day.dayNumber }}</div>
            <div class="timeline-day-events">
              <span v-if="getEventsForDay(user, day.isoDate).length === 0" class="timeline-day-empty">
                {{ tt("twitchPostAdmin.noPosts", "sin publicaciones") }}
              </span>
              <span
                v-for="event in getEventsForDay(user, day.isoDate)"
                :key="event.clip_id"
                class="timeline-day-event-pill"
                :title="`${event.posting_time ? `${formatTimelineTime12h(event.posting_time)} · ` : ''}${event.title}`"
                @click.stop="openEventPreview(event, user)"
              >
                {{ event.posting_time ? `${formatTimelineTime12h(event.posting_time)} · ` : "" }}{{ event.title }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <body-portal>
      <div
        v-if="showAssignAdminModal && assignTargetUser"
        class="timeline-preview-modal-backdrop"
        @click="closeAssignAdminModal()"
      >
        <div class="timeline-assign-modal" @click.stop>
          <div class="timeline-assign-modal-header">
            <div class="timeline-assign-header-icon">
              <i class="ti-user"></i>
            </div>
            <div class="timeline-assign-header-copy">
              <h5 class="timeline-preview-modal-title mb-1">{{ tt("twitchPostAdmin.assignAdminTitle", "Asignar admin") }}</h5>
            </div>
            <button
              type="button"
              class="timeline-preview-close"
              :aria-label="tt('twitchPostAdmin.closeAssignAdmin', 'Cerrar asignación de admin')"
              :disabled="assigningAdmin"
              @click="closeAssignAdminModal()"
            >
              <i class="ti-close"></i>
            </button>
          </div>
          <div class="timeline-preview-modal-body timeline-assign-modal-body">
            <div class="timeline-assign-target-card">
              <span class="timeline-assign-target-label">{{ tt("twitchPostAdmin.timelineUser", "Assign user") }}: {{ getAssignedAdminName(assignTargetUser) }}</span>
              <strong>{{ getTimelineUserName(assignTargetUser) }}</strong>
              <small>{{ assignTargetUser.use_timezone || "UTC" }}</small>
            </div>
            <div v-if="adminUsersLoading" class="timeline-preview-state text-center p-3">
              <i class="ti-reload rotating"></i>
              <p class="mb-0 mt-2">{{ tt("twitchPostAdmin.loadingAdmins", "Cargando admins...") }}</p>
            </div>
            <div v-else-if="adminUsersError" class="alert alert-danger mb-0">
              {{ adminUsersError }}
            </div>
            <div v-else-if="adminUsers.length === 0" class="timeline-preview-state text-center p-3">
              <p class="mb-0 text-muted">{{ tt("twitchPostAdmin.noAdminsAvailable", "No hay admins disponibles.") }}</p>
            </div>
            <div v-else class="timeline-admin-picker">
              <div class="timeline-admin-search-wrap">
                <i class="ti-search"></i>
                <input
                  v-model="adminSearchInput"
                  type="text"
                  class="timeline-admin-search-input"
                  :placeholder="tt('twitchPostAdmin.searchAdminPlaceholder', 'Buscar admin')"
                  :disabled="assigningAdmin"
                />
              </div>
              <div v-if="filteredAdminUsers.length === 0" class="timeline-preview-state text-center p-3">
                <p class="mb-0 text-muted">{{ tt("twitchPostAdmin.noAdminsMatchFilter", "Ningún admin coincide con la búsqueda.") }}</p>
              </div>
              <div v-else class="timeline-admin-list">
                <button
                  v-for="admin in filteredAdminUsers"
                  :key="`timeline-admin-${admin.user_id}`"
                  type="button"
                  class="timeline-admin-option"
                  :class="{ selected: selectedAdminUserId === admin.user_id }"
                  :disabled="assigningAdmin"
                  @click="selectAdminUser(admin)"
                >
                  <span class="timeline-admin-option-avatar">{{ getAdminInitial(admin) }}</span>
                  <span class="timeline-admin-option-copy">
                    <strong>{{ getAdminName(admin) }}</strong>
                    <small>{{ admin.use_timezone || "UTC" }}</small>
                  </span>
                  <span class="timeline-admin-option-check">
                    <i v-if="selectedAdminUserId === admin.user_id" class="ti-check"></i>
                  </span>
                </button>
              </div>
            </div>
            <div class="timeline-assign-actions">
              <button
                type="button"
                class="timeline-manage-close-btn"
                :disabled="assigningAdmin"
                @click="closeAssignAdminModal()"
              >
                {{ tt("common.close", "Cerrar") }}
              </button>
              <button
                type="button"
                class="timeline-admin-btn"
                :disabled="assigningAdmin || !selectedAdminUserId"
                @click="assignSelectedAdmin"
              >
                <span v-if="assigningAdmin">
                  <i class="ti-reload rotating" style="margin-right: 6px;"></i>
                  {{ tt("twitchPostAdmin.assigningAdmin", "Asignando...") }}
                </span>
                <span v-else>{{ tt("twitchPostAdmin.assignAdmin", "Assign") }}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </body-portal>

    <body-portal>
      <div
        v-if="previewModalOpen"
        class="timeline-preview-modal-backdrop"
        @click="closeEventPreview"
      >
        <div class="timeline-preview-modal" @click.stop>
        <div class="timeline-preview-modal-header">
          <div>
            <h5 class="timeline-preview-modal-title mb-1">{{ tt("twitchPostAdmin.previewTitle", "Preview de clip programado") }}</h5>
            <p class="timeline-preview-modal-subtitle mb-0">
              {{ selectedPreviewEvent?.title || tt("twitchPostAdmin.untitledClip", "Clip sin título") }}
            </p>
          </div>
          <button
            type="button"
            class="timeline-preview-close"
            :aria-label="tt('twitchPostAdmin.closePreview', 'Cerrar preview')"
            @click="closeEventPreview"
          >
            <i class="ti-close"></i>
          </button>
        </div>

        <div class="timeline-preview-modal-body">
          <div v-if="previewLoading" class="timeline-preview-state text-center p-3">
            <i class="ti-reload rotating"></i>
            <p class="mb-0 mt-2">{{ tt("common.loading", "Cargando...") }}</p>
          </div>

          <div v-else-if="previewError" class="alert alert-danger mb-0">
            {{ previewError }}
          </div>

          <div v-else-if="previewClip" class="timeline-preview-content">
            <video
              v-if="previewClip.videoUrl"
              class="timeline-preview-video"
              :src="previewClip.videoUrl"
              controls
              playsinline
              preload="metadata"
            ></video>
            <div v-else class="timeline-preview-no-video">
              <i class="ti-video-camera"></i>
              <span>{{ tt("twitchPostAdmin.noVideo", "No hay video disponible para este clip.") }}</span>
            </div>

            <div class="timeline-preview-meta">
              <p class="mb-1">
                <strong>{{ tt("twitchPostAdmin.user", "Usuario") }}:</strong>
                {{ previewClip.userName || selectedPreviewEvent?.user_name || "N/A" }}
              </p>
              <p class="mb-1">
                <strong>{{ tt("twitchPostAdmin.time", "Hora") }}:</strong>
                {{ selectedPreviewEvent?.posting_time || "N/A" }}
              </p>
              <p class="mb-1">
                <strong>{{ tt("twitchPostAdmin.status", "Estado") }}:</strong>
                {{ previewClip.status || selectedPreviewEvent?.status || "N/A" }}
              </p>
              <div v-if="previewSocialUrls.length" class="timeline-preview-social-urls">
                <strong>{{ tt("twitchPostAdmin.publishedUrls", "URLs publicadas") }}:</strong>
                <ul class="timeline-preview-social-list mb-0">
                  <li
                    v-for="social in previewSocialUrls"
                    :key="social.platform"
                    class="timeline-preview-social-item"
                  >
                    <span class="timeline-preview-social-label">{{ social.label }}:</span>
                    <a :href="social.url" target="_blank" rel="noopener noreferrer">
                      {{ social.url }}
                    </a>
                  </li>
                </ul>
              </div>
              <p class="mb-0" v-if="previewClip.streamName">
                <strong>{{ tt("twitchPostAdmin.stream", "Stream") }}:</strong> {{ previewClip.streamName }}
              </p>
            </div>

            <div class="timeline-preview-actions">
              <button
                type="button"
                class="timeline-admin-btn"
                @click="openManageScheduledModalFromPreview"
              >
                <i class="ti-settings" style="margin-right: 6px;"></i>
                {{ tt("common.manage", "Administrar") }}
              </button>
            </div>
          </div>
        </div>
        </div>
      </div>
    </body-portal>

    <body-portal>
      <div
        v-if="showManageScheduledModal && selectedScheduledClip"
        class="timeline-preview-modal-backdrop"
        @click="closeManageScheduledModal"
      >
        <div class="timeline-manage-modal" @click.stop>
        <div class="timeline-preview-modal-header">
          <div>
            <h5 class="timeline-preview-modal-title mb-1">{{ tt("twitchPostAdmin.manageScheduledClip", "Administrar clip programado") }}</h5>
            <p class="timeline-preview-modal-subtitle mb-0">
              {{ selectedScheduledClip.title || tt("twitchPostAdmin.untitledClip", "Clip sin título") }}
            </p>
          </div>
          <button
            type="button"
            class="timeline-preview-close"
            :aria-label="tt('twitchPostAdmin.closeManageScheduledClip', 'Cerrar administrar clip')"
            :disabled="manageScheduledCancelling || manageScheduledSaving"
            @click="closeManageScheduledModal"
          >
            <i class="ti-close"></i>
          </button>
        </div>
        <div class="timeline-preview-modal-body">
          <div class="scheduled-clip-grid">
            <div class="scheduled-clip-item">
              <span class="scheduled-clip-label">{{ tt("twitchPostAdmin.clipId", "ID del clip") }}</span>
              <strong class="scheduled-clip-value">{{ selectedScheduledClip.id }}</strong>
            </div>
            <div class="scheduled-clip-item">
              <span class="scheduled-clip-label">{{ tt("twitchPostAdmin.status", "Estado") }}</span>
              <strong class="scheduled-clip-value">{{ selectedScheduledClip.status || "N/A" }}</strong>
            </div>
            <div class="scheduled-clip-item">
              <span class="scheduled-clip-label">{{ tt("twitchPostAdmin.user", "Usuario") }}</span>
              <strong class="scheduled-clip-value">{{ selectedScheduledClip.user_name || "N/A" }}</strong>
            </div>
            <div class="scheduled-clip-item">
              <span class="scheduled-clip-label">{{ tt("twitchPostAdmin.scheduledDate", "Fecha programada") }}</span>
              <strong class="scheduled-clip-value">{{ getScheduledClipDateLabel(selectedScheduledClip) }}</strong>
            </div>
            <div class="scheduled-clip-item">
              <span class="scheduled-clip-label">{{ tt("twitchPostAdmin.scheduledTime", "Hora programada") }}</span>
              <strong class="scheduled-clip-value">
                {{ getScheduledClipTimeLabel(selectedScheduledClip) }} ({{ getScheduledClipTimezoneLabel(selectedScheduledClip) }})
              </strong>
            </div>
          </div>

          <div
            v-if="canEditScheduledClip(selectedScheduledClip) && manageScheduledEditDraft"
            class="timeline-scheduled-editor"
          >
            <div class="timeline-scheduled-editor-header">
              <strong>{{ tt("schedulePost.editScheduledMetadata", "Editar programación") }}</strong>
              <small>{{ tt("schedulePost.manageScheduledSubtitle", "Detalles y acciones para esta publicación.") }}</small>
            </div>
            <div class="timeline-scheduled-editor-grid">
              <label class="timeline-scheduled-editor-field timeline-scheduled-editor-field--full">
                <span>{{ tt("schedulePost.title", "Título") }}</span>
                <input
                  v-model="manageScheduledEditDraft.title"
                  type="text"
                  class="timeline-scheduled-editor-input"
                  :disabled="manageScheduledSaving || manageScheduledCancelling"
                />
              </label>
              <label class="timeline-scheduled-editor-field timeline-scheduled-editor-field--full">
                <span>{{ tt("schedulePost.description", "Descripción") }}</span>
                <textarea
                  v-model="manageScheduledEditDraft.description"
                  rows="3"
                  class="timeline-scheduled-editor-input timeline-scheduled-editor-textarea"
                  :disabled="manageScheduledSaving || manageScheduledCancelling"
                ></textarea>
              </label>
              <label class="timeline-scheduled-editor-field timeline-scheduled-editor-field--full">
                <span>{{ tt("schedulePost.tags", "Etiquetas") }}</span>
                <input
                  v-model="manageScheduledEditDraft.tags"
                  type="text"
                  class="timeline-scheduled-editor-input"
                  :placeholder="tt('schedulePost.tagsPlaceholder', '#tag1, #tag2')"
                  :disabled="manageScheduledSaving || manageScheduledCancelling"
                />
              </label>
              <label class="timeline-scheduled-editor-field">
                <span>{{ tt("twitchPostAdmin.scheduledDate", "Fecha programada") }}</span>
                <input
                  v-model="manageScheduledEditDraft.date"
                  type="date"
                  class="timeline-scheduled-editor-input"
                  :disabled="manageScheduledSaving || manageScheduledCancelling"
                />
              </label>
              <label class="timeline-scheduled-editor-field">
                <span>{{ tt("twitchPostAdmin.scheduledTime", "Hora programada") }}</span>
                <input
                  v-model="manageScheduledEditDraft.time"
                  type="time"
                  class="timeline-scheduled-editor-input"
                  :disabled="manageScheduledSaving || manageScheduledCancelling"
                />
              </label>
              <div class="timeline-scheduled-editor-field">
                <span>{{ tt("schedulePost.timezone", "Zona horaria") }}</span>
                <strong class="timeline-scheduled-editor-timezone">
                  {{ manageScheduledEditDraft.timezone }}
                </strong>
              </div>
            </div>
          </div>

          <div v-if="manageScheduledError" class="alert alert-danger mt-3 mb-0">
            {{ manageScheduledError }}
          </div>

          <div class="timeline-manage-actions">
            <button
              v-if="canCancelScheduledUpload(selectedScheduledClip)"
              type="button"
              class="timeline-cancel-upload-btn"
              :disabled="manageScheduledCancelling || manageScheduledSaving"
              @click="cancelScheduledUpload"
            >
              <span v-if="manageScheduledCancelling">
                <i class="ti-reload rotating" style="margin-right: 6px;"></i>
                {{ tt("twitchPostAdmin.cancellingUpload", "CANCELANDO...") }}
              </span>
              <span v-else>
                <i class="ti-na" style="margin-right: 6px;"></i>
                {{ tt("twitchPostAdmin.cancelUpload", "CANCELAR SUBIDA") }}
              </span>
            </button>

            <button
              v-if="canEditScheduledClip(selectedScheduledClip)"
              type="button"
              class="timeline-admin-btn"
              :disabled="manageScheduledCancelling || manageScheduledSaving || !canSaveScheduledEdit()"
              @click="saveScheduledClipEdit"
            >
              <span v-if="manageScheduledSaving">
                <i class="ti-reload rotating" style="margin-right: 6px;"></i>
                {{ tt("schedulePost.saving", "Guardando...") }}
              </span>
              <span v-else>
                <i class="ti-save" style="margin-right: 6px;"></i>
                {{ tt("schedulePost.saveScheduledChanges", "Guardar cambios") }}
              </span>
            </button>

            <button
              type="button"
              class="timeline-manage-close-btn"
              :disabled="manageScheduledCancelling || manageScheduledSaving"
              @click="closeManageScheduledModal"
            >
              {{ tt("makeClips.close", "CERRAR") }}
            </button>
          </div>
        </div>
        </div>
      </div>
    </body-portal>
  </div>
</template>

<script>
import { supabase } from "@/supabase";
import {
  buildAuthHeaders,
  getImpersonation,
  IMPERSONATION_EVENT,
  startImpersonation,
} from "@/utils/impersonation";
import escapeToCloseMixin from "@/mixins/escapeToCloseMixin";
import { useCurrentUserStore } from "@/stores/currentUser";
import { updateTwitchClip } from "@/api/twitch";

export default {
  name: "TwitchPostadmin",
  mixins: [escapeToCloseMixin],
  data() {
    return {
      loading: false,
      errorMessage: "",
      users: [],
      usernameFilterInput: "",
      activeUsernameFilter: "",
      showUsernameSuggestions: false,
      timelineUserScope: "mine",
      showAssignAdminModal: false,
      assignTargetUser: null,
      adminUsers: [],
      adminUsersLoading: false,
      adminUsersError: "",
      adminSearchInput: "",
      selectedAdminUserId: "",
      assigningAdmin: false,
      totalEvents: 0,
      timelineStartDate: "",
      timelineVisibleDays: 8,
      previewModalOpen: false,
      previewLoading: false,
      previewError: "",
      selectedPreviewEvent: null,
      previewClip: null,
      showManageScheduledModal: false,
      selectedScheduledClip: null,
      manageScheduledCancelling: false,
      manageScheduledSaving: false,
      manageScheduledError: "",
      manageScheduledEditDraft: null,
      impersonation: getImpersonation(),
    };
  },
  computed: {
    isTwitchUser() {
      return useCurrentUserStore().isTwitchUser;
    },
    isAdminUser() {
      return String(useCurrentUserStore().useRole || "").trim().toLowerCase() === "admin";
    },
    currentAdminUserId() {
      return useCurrentUserStore().currentUserId || "";
    },
    escapeToCloseModals() {
      return [
        { show: "showAssignAdminModal", close: "closeAssignAdminModal" },
        { show: "previewModalOpen", close: "closeEventPreview" },
        { show: "showManageScheduledModal", close: "closeManageScheduledModal" },
      ];
    },
    totalReadyToPost() {
      return this.timelineUsers.reduce((acc, user) => acc + Number(user?.readyToPostCount || 0), 0);
    },
    totalPosted() {
      return this.timelineUsers.reduce((acc, user) => acc + Number(user?.postedCount || 0), 0);
    },
    visibleTotalEvents() {
      return this.timelineUsers.reduce(
        (acc, user) => acc + (Array.isArray(user?.events) ? user.events.length : 0),
        0
      );
    },
    activeUsernameFilterNormalized() {
      return this.normalizeUsernameFilter(this.activeUsernameFilter);
    },
    filteredAdminUsers() {
      const query = this.normalizeUsernameFilter(this.adminSearchInput);
      if (!query) return this.adminUsers;
      return this.adminUsers.filter((admin) => this.getAdminSearchText(admin).includes(query));
    },
    usernameSuggestions() {
      const query = this.normalizeUsernameFilter(this.usernameFilterInput);
      if (!query) return [];
      return this.scopeFilteredTimelineUsers
        .filter((user) => {
          const userName = this.getTimelineUserName(user);
          if (!userName) return false;
          return this.getTimelineUserSearchText(user).includes(query);
        })
        .slice(0, 8);
    },
    scopeFilteredTimelineUsers() {
      if (this.timelineUserScope !== "mine") return this.users;
      const currentAdminUserId = this.normalizeUsernameFilter(this.currentAdminUserId);
      if (!currentAdminUserId) return [];
      return this.users.filter(
        (user) => this.normalizeUsernameFilter(user?.assigned_admin) === currentAdminUserId
      );
    },
    timelineUsers() {
      if (!this.activeUsernameFilterNormalized) return this.scopeFilteredTimelineUsers;
      return this.scopeFilteredTimelineUsers.filter((user) => {
        const searchText = this.getTimelineUserSearchText(user);
        return searchText.includes(this.activeUsernameFilterNormalized);
      });
    },
    timelineDays() {
      const days = [];
      if (!this.timelineStartDate) return days;

      const parts = String(this.timelineStartDate).split("-").map(Number);
      if (parts.length !== 3 || parts.some((x) => Number.isNaN(x))) return days;

      const start = new Date(parts[0], parts[1] - 1, parts[2], 12, 0, 0);
      for (let i = 0; i < this.timelineVisibleDays; i += 1) {
        const date = new Date(start);
        date.setDate(start.getDate() + i);

        const isoDate = [
          date.getFullYear(),
          String(date.getMonth() + 1).padStart(2, "0"),
          String(date.getDate()).padStart(2, "0"),
        ].join("-");

        const todayStr = this.getTodayDateStr();
        days.push({
          isoDate,
          dayNumber: new Intl.DateTimeFormat(this.$i18n?.locale || "es", { day: "2-digit" }).format(date),
          weekdayLabel: new Intl.DateTimeFormat(this.$i18n?.locale || "es", { weekday: "short" }).format(date),
          isToday: isoDate === todayStr,
        });
      }

      return days;
    },
    timelineMonthLabel() {
      if (!this.timelineDays || this.timelineDays.length === 0) return "";
      const first = this.timelineDays[0];
      const parts = String(first?.isoDate || "").split("-").map(Number);
      if (parts.length !== 3 || parts.some((x) => Number.isNaN(x))) return "";
      const date = new Date(parts[0], parts[1] - 1, parts[2]);
      return new Intl.DateTimeFormat(this.$i18n?.locale || "es", { month: "long" }).format(date).toUpperCase();
    },
    previewSocialUrls() {
      const rawOutputUrls = this.previewClip?.outputUrls || this.selectedPreviewEvent?.output_urls || null;
      return this.extractSocialUrls(rawOutputUrls);
    },
  },
  async mounted() {
    this.timelineStartDate = this.getTodayDateStr();
    this.calculateTimelineVisibleDays();
    window.addEventListener("resize", this.calculateTimelineVisibleDays);
    window.addEventListener(IMPERSONATION_EVENT, this.syncImpersonation);
    await this.loadTimeline();
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.calculateTimelineVisibleDays);
    window.removeEventListener(IMPERSONATION_EVENT, this.syncImpersonation);
  },
  methods: {
    tt(key, fallback) {
      return typeof this.$t === "function" ? this.$t(key) : fallback;
    },
    normalizeUsernameFilter(value) {
      return String(value || "").trim().toLowerCase();
    },
    getTimelineUserName(user) {
      return String(user?.use_name || user?.user_id || "").trim();
    },
    getAdminName(admin) {
      return String(admin?.use_name || admin?.user_id || "").trim();
    },
    getAdminInitial(admin) {
      const name = this.getAdminName(admin);
      return name ? name.charAt(0).toUpperCase() : "?";
    },
    getAdminSearchText(admin) {
      return [
        this.getAdminName(admin),
        admin?.user_id,
      ]
        .map((value) => this.normalizeUsernameFilter(value))
        .filter(Boolean)
        .join(" ");
    },
    getAssignedAdminName(user) {
      const assignedAdmin = String(user?.assigned_admin || "").trim();
      if (!assignedAdmin) return this.tt("twitchPostAdmin.unassignedAdmin", "Sin asignar");
      const assignedAdminName = String(user?.assigned_admin_name || "").trim();
      if (assignedAdminName) return assignedAdminName;
      const admin = this.adminUsers.find((item) => String(item?.user_id || "") === assignedAdmin);
      return this.getAdminName(admin) || this.tt("twitchPostAdmin.assignedAdminUnknown", "Admin asignado");
    },
    getTimelineUserSearchText(user) {
      return [
        this.getTimelineUserName(user),
        user?.user_id,
      ]
        .map((value) => this.normalizeUsernameFilter(value))
        .filter(Boolean)
        .join(" ");
    },
    isTimelineUserAssigned(user) {
      return Boolean(String(user?.assigned_admin || "").trim());
    },
    applyUsernameFilter() {
      this.applyUsernameFilterValue(this.usernameFilterInput);
    },
    applyUsernameFilterValue(value) {
      const rawFilter = String(value || "").trim();
      const normalizedFilter = this.normalizeUsernameFilter(rawFilter);
      if (!normalizedFilter) return;
      this.usernameFilterInput = rawFilter;
      this.activeUsernameFilter = rawFilter;
      this.showUsernameSuggestions = false;
    },
    handleUsernameFilterInput() {
      this.showUsernameSuggestions = true;
      this.activeUsernameFilter = String(this.usernameFilterInput || "").trim();
    },
    selectUsernameSuggestion(user) {
      const userName = this.getTimelineUserName(user);
      if (!userName) return;
      this.applyUsernameFilterValue(userName);
    },
    hideUsernameSuggestions() {
      this.showUsernameSuggestions = false;
    },
    clearTimelineUsernameSearch() {
      this.activeUsernameFilter = "";
      this.usernameFilterInput = "";
      this.showUsernameSuggestions = false;
    },
    setTimelineUserScope(scope) {
      this.clearTimelineUsernameSearch();
      this.timelineUserScope = scope;
    },
    async openAssignAdminModal(user) {
      if (!user?.user_info_id) return;
      this.clearTimelineUsernameSearch();
      this.assignTargetUser = user;
      this.selectedAdminUserId = String(user?.assigned_admin || this.currentAdminUserId || "");
      this.adminSearchInput = "";
      this.adminUsersError = "";
      this.showAssignAdminModal = true;
      if (this.adminUsers.length === 0) {
        await this.fetchPostAdminAdmins();
      }
    },
    closeAssignAdminModal(force = false) {
      if (this.assigningAdmin && !force) return;
      this.showAssignAdminModal = false;
      this.assignTargetUser = null;
      this.selectedAdminUserId = "";
      this.adminSearchInput = "";
      this.adminUsersError = "";
    },
    selectAdminUser(admin) {
      if (!admin?.user_id) return;
      this.selectedAdminUserId = admin.user_id;
    },
    async fetchPostAdminAdmins() {
      this.adminUsersLoading = true;
      this.adminUsersError = "";
      try {
        const apiUrl = process.env.VUE_APP_API_URL;
        if (!apiUrl) throw new Error("Falta VUE_APP_API_URL");
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (!session) throw new Error("Sesion no valida");

        const response = await fetch(`${apiUrl}/momo/twitch/post-admin/admins`, {
          method: "GET",
          headers: buildAuthHeaders(session, { includeContentType: false }),
        });
        if (!response.ok) {
          const detail = await response.text().catch(() => "");
          throw new Error(detail || "No fue posible cargar los admins");
        }

        const payload = await response.json();
        this.adminUsers = Array.isArray(payload?.admins) ? payload.admins : [];
      } catch (error) {
        this.adminUsers = [];
        this.adminUsersError = error?.message || "No fue posible cargar los admins";
      } finally {
        this.adminUsersLoading = false;
      }
    },
    async assignSelectedAdmin() {
      if (!this.assignTargetUser?.user_info_id || !this.selectedAdminUserId) return;
      this.assigningAdmin = true;
      this.adminUsersError = "";
      try {
        const apiUrl = process.env.VUE_APP_API_URL;
        if (!apiUrl) throw new Error("Falta VUE_APP_API_URL");
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (!session) throw new Error("Sesion no valida");

        const response = await fetch(
          `${apiUrl}/momo/twitch/post-admin/users/${encodeURIComponent(this.assignTargetUser.user_info_id)}/assigned-admin`,
          {
            method: "PATCH",
            headers: buildAuthHeaders(session),
            body: JSON.stringify({ assigned_admin: this.selectedAdminUserId }),
          }
        );
        if (!response.ok) {
          const errData = await response.json().catch(() => null);
          throw new Error(errData?.detail || "No fue posible asignar el admin");
        }
        const payload = await response.json();
        const assignedAdmin = String(payload?.assigned_admin || this.selectedAdminUserId);
        const selectedAdmin = this.adminUsers.find((admin) => String(admin?.user_id || "") === assignedAdmin);
        const assignedAdminName = this.getAdminName(selectedAdmin);
        const targetUserInfoId = Number(this.assignTargetUser.user_info_id);
        this.users = this.users.map((user) => (
          Number(user?.user_info_id) === targetUserInfoId
            ? { ...user, assigned_admin: assignedAdmin, assigned_admin_name: assignedAdminName }
            : user
        ));
        this.closeAssignAdminModal(true);
      } catch (error) {
        this.adminUsersError = error?.message || "No fue posible asignar el admin";
      } finally {
        this.assigningAdmin = false;
      }
    },
    syncImpersonation() {
      this.impersonation = getImpersonation();
    },
    canImpersonateUsers() {
      return this.isTwitchUser === true;
    },
    isImpersonatingRow(user) {
      const active = this.impersonation;
      if (!active || !user?.user_info_id) return false;
      return String(active.userInfoId) === String(user.user_info_id);
    },
    async impersonateTimelineUser(user) {
      if (!user?.user_info_id) return;
      this.clearTimelineUsernameSearch();
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (!session) {
          this.$router.push("/login");
          return;
        }
        const apiUrl = process.env.VUE_APP_API_URL || "http://localhost:8000";
        const failMsg = this.tt("usersList.impersonationFailed", "No se pudo iniciar suplantación de usuario");
        await startImpersonation({
          session,
          userInfoId: user.user_info_id,
          username: user.use_name || "",
          email: "",
          apiUrl,
          impersonationFailedMessage: failMsg,
        });
        await useCurrentUserStore().refresh();
        this.impersonation = getImpersonation();
        this.$notify({
          message: this.tt("usersList.impersonationStarted", "Suplantación iniciada."),
          type: "success",
        });
        this.$router.push("/create-clips");
      } catch (error) {
        console.error("Error impersonating user:", error);
        this.$notify({
          message: error.message || this.tt("usersList.impersonationFailed", "No se pudo iniciar suplantación de usuario"),
          type: "danger",
        });
      }
    },
    normalizeClipStatus(status) {
      return String(status || "").trim().toLowerCase();
    },
    isTimelineStatus(status) {
      const normalized = this.normalizeClipStatus(status);
      return normalized === "5_scheduled_to_post" || normalized === "7_posted";
    },
    getTodayDateStr() {
      const now = new Date();
      const y = now.getFullYear();
      const m = String(now.getMonth() + 1).padStart(2, "0");
      const d = String(now.getDate()).padStart(2, "0");
      return `${y}-${m}-${d}`;
    },
    calculateTimelineVisibleDays() {
      const container = this.$refs.timelineSizer;
      if (!container || typeof container.clientWidth !== "number") return;
      const cardMinWidth = 140;
      const cardGap = 10;
      const nextVisibleDays = Math.max(
        5,
        Math.floor((container.clientWidth + cardGap) / (cardMinWidth + cardGap))
      );
      if (nextVisibleDays !== this.timelineVisibleDays) {
        this.timelineVisibleDays = nextVisibleDays;
      }
    },
    shiftTimeline(direction) {
      if (!direction || !this.timelineStartDate) return;
      this.clearTimelineUsernameSearch();
      const parts = String(this.timelineStartDate).split("-").map(Number);
      if (parts.length !== 3 || parts.some((x) => Number.isNaN(x))) return;
      const date = new Date(parts[0], parts[1] - 1, parts[2], 12, 0, 0);
      date.setDate(date.getDate() + direction * this.timelineVisibleDays);
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, "0");
      const d = String(date.getDate()).padStart(2, "0");
      this.timelineStartDate = `${y}-${m}-${d}`;
    },
    async loadTimeline() {
      this.loading = true;
      this.errorMessage = "";
      try {
        const apiUrl = process.env.VUE_APP_API_URL;
        if (!apiUrl) throw new Error("Falta VUE_APP_API_URL");

        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (!session) throw new Error("Sesion no valida");

        const response = await fetch(`${apiUrl}/momo/twitch/post-admin-timeline`, {
          method: "GET",
          headers: buildAuthHeaders(session, { includeContentType: false }),
        });
        if (!response.ok) {
          const detail = await response.text().catch(() => "");
          throw new Error(detail || "No fue posible cargar la timeline");
        }

        const payload = await response.json();
        const users = Array.isArray(payload?.users) ? payload.users : [];

        this.users = users.map((user) => {
          const events = Array.isArray(user?.events) ? user.events : [];
          const sortedEvents = [...events].sort((a, b) => {
            const aDate = String(a?.posting_at || "");
            const bDate = String(b?.posting_at || "");
            return aDate.localeCompare(bDate);
          });
          const timelineEvents = sortedEvents.filter((event) => this.isTimelineStatus(event?.status));
          const readyToPostCount = timelineEvents.filter(
            (event) => this.normalizeClipStatus(event?.status) === "5_scheduled_to_post"
          ).length;
          const postedCount = timelineEvents.filter(
            (event) => this.normalizeClipStatus(event?.status) === "7_posted"
          ).length;
          const eventsByDate = timelineEvents.reduce((acc, event) => {
            const localized = this.getEventDatePartsByTimezone(
              event?.posting_at,
              user?.use_timezone
            );
            const dateKey = localized.dateStr || String(event?.posting_date || "");
            if (!dateKey) return acc;
            const eventWithLocalTime = {
              ...event,
              posting_date: dateKey,
              posting_time: localized.timeStr || String(event?.posting_time || ""),
            };
            if (!acc[dateKey]) acc[dateKey] = [];
            acc[dateKey].push(eventWithLocalTime);
            return acc;
          }, {});
          return {
            ...user,
            events: timelineEvents,
            eventsByDate,
            readyToPostCount,
            postedCount,
          };
        });

        this.totalEvents = this.users.reduce(
          (acc, user) => acc + (Array.isArray(user?.events) ? user.events.length : 0),
          0
        );
      } catch (error) {
        this.users = [];
        this.totalEvents = 0;
        this.errorMessage = error?.message || "Error inesperado";
      } finally {
        this.loading = false;
      }
    },
    getEventsForDay(user, isoDate) {
      if (!user || !user.eventsByDate) return [];
      return user.eventsByDate[isoDate] || [];
    },
    closeEventPreview() {
      this.previewModalOpen = false;
      this.previewLoading = false;
      this.previewError = "";
      this.selectedPreviewEvent = null;
      this.previewClip = null;
      this.closeManageScheduledModal(true);
    },
    async openEventPreview(event, user) {
      if (!event || !event.clip_id) return;
      this.clearTimelineUsernameSearch();

      this.previewModalOpen = true;
      this.previewLoading = true;
      this.previewError = "";
      this.previewClip = null;
      this.selectedPreviewEvent = {
        ...event,
        user_name: user?.use_name || user?.user_id || null,
        use_timezone: user?.use_timezone || "UTC",
      };

      try {
        const apiUrl = process.env.VUE_APP_API_URL;
        if (!apiUrl) throw new Error("Falta VUE_APP_API_URL");
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (!session) throw new Error("Sesion no valida");

        const response = await fetch(
          `${apiUrl}/momo/twitch/post-admin/clips/${encodeURIComponent(event.clip_id)}/preview`,
          {
            method: "GET",
            headers: buildAuthHeaders(session, { includeContentType: false }),
          }
        );
        if (!response.ok) {
          const errData = await response.json().catch(() => ({ detail: response.statusText }));
          throw new Error(errData.detail || this.tt("twitchPostAdmin.previewLoadError", "No fue posible cargar la preview del clip."));
        }
        const payload = await response.json();
        const clipData = payload?.clip || null;
        const jobData = payload?.job || null;

        const streamName =
          (jobData?.source_meta && jobData.source_meta.title) ||
          null;

        this.previewClip = {
          id: clipData?.id || event?.clip_id || null,
          videoUrl: clipData?.rendered_video_url || null,
          status: clipData?.status || event?.status || null,
          title: clipData?.title || event?.title || null,
          userName: jobData?.source_username || event?.source_username || null,
          postingAt: clipData?.posting_at || event?.posting_at || null,
          userTimezone: user?.use_timezone || event?.use_timezone || "UTC",
          user_name: user?.use_name || user?.user_id || null,
          streamName,
          outputUrls: this.normalizeOutputUrls(clipData?.output_urls || event?.output_urls || null),
          clip_metadata: this.normalizeClipMetadata(clipData?.clip_metadata || null),
          clip_tags: Array.isArray(clipData?.clip_tags) ? clipData.clip_tags : [],
        };

        if (!this.previewClip.videoUrl) {
          this.previewError = this.tt("twitchPostAdmin.previewUrlMissing", "No se encontró URL de preview para este clip programado.");
        }
      } catch (error) {
        this.previewError = error?.message || this.tt("twitchPostAdmin.previewLoadError", "No fue posible cargar la preview del clip.");
      } finally {
        this.previewLoading = false;
      }
    },
    getIanaTimezone(timezoneValue) {
      const raw = String(timezoneValue || "").trim();
      if (!raw) return "UTC";
      if (raw.includes("/")) return raw;
      const normalized = raw.toUpperCase();
      const map = {
        // Use fixed offsets so abbreviations do not drift with DST.
        PST: "Etc/GMT+8",
        PDT: "Etc/GMT+7",
        MST: "Etc/GMT+7",
        MDT: "Etc/GMT+6",
        CST: "Etc/GMT+6",
        CDT: "Etc/GMT+5",
        EST: "Etc/GMT+5",
        EDT: "Etc/GMT+4",
        UTC: "UTC",
      };
      return map[normalized] || "UTC";
    },
    parsePostingAtDate(postingAt) {
      if (!postingAt) return null;
      const raw = String(postingAt).trim();
      if (!raw) return null;
      const normalized = raw.replace(" ", "T").replace(/([+-]\d{2})$/, "$1:00");
      const date = new Date(normalized);
      if (!isNaN(date.getTime())) return date;
      const fallback = new Date(raw);
      return !isNaN(fallback.getTime()) ? fallback : null;
    },
    getEventDatePartsByTimezone(postingAt, timezoneValue) {
      const dateObj = this.parsePostingAtDate(postingAt);
      if (!dateObj) return { dateStr: "", timeStr: "" };
      const tzIana = this.getIanaTimezone(timezoneValue);
      const dateStr = new Intl.DateTimeFormat("en-CA", {
        timeZone: tzIana,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }).format(dateObj);
      const timeStr = new Intl.DateTimeFormat(this.$i18n?.locale === "en" ? "en-GB" : "es-ES", {
        timeZone: tzIana,
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      }).format(dateObj);
      return { dateStr, timeStr };
    },
    formatTimelineTime12h(timeStr) {
      const raw = String(timeStr || "").trim();
      if (!raw) return "";
      const match = raw.match(/^(\d{1,2}):(\d{2})$/);
      if (!match) return raw;

      const hour24 = Number(match[1]);
      const minute = match[2];
      if (Number.isNaN(hour24) || hour24 < 0 || hour24 > 23) return raw;

      const period = hour24 >= 12 ? "pm" : "am";
      const hour12 = hour24 % 12 || 12;
      return `${hour12}:${minute} ${period}`;
    },
    normalizeOutputUrls(value) {
      if (!value) return null;
      if (typeof value === "object" && !Array.isArray(value)) return value;
      if (typeof value === "string") {
        try {
          const parsed = JSON.parse(value);
          if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) return parsed;
        } catch (error) {
          return null;
        }
      }
      return null;
    },
    normalizeClipMetadata(value) {
      if (!value) return {};
      if (typeof value === "object" && !Array.isArray(value)) return { ...value };
      if (typeof value === "string") {
        try {
          const parsed = JSON.parse(value);
          if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) return parsed;
        } catch (error) {
          return {};
        }
      }
      return {};
    },
    parseCommaList(text) {
      if (!text || typeof text !== "string") return [];
      return text
        .split(",")
        .map((item) => item.trim())
        .filter((item) => item.length > 0);
    },
    composeDescriptionWithHashtags(descriptionText, hashtagsText) {
      const description = String(descriptionText || "").trim();
      const hashtags = this.parseCommaList(hashtagsText)
        .map((tag) => (tag.startsWith("#") ? tag : `#${tag}`))
        .join(" ")
        .trim();
      if (description && hashtags) return `${description}\n\n${hashtags}`;
      if (hashtags) return hashtags;
      return description;
    },
    normalizeClipTagsText(clipTags) {
      if (Array.isArray(clipTags)) {
        return clipTags
          .map((tag) => String(tag || "").trim())
          .filter((tag) => tag.length > 0)
          .join(", ");
      }
      if (typeof clipTags !== "string") return "";
      return clipTags
        .replace(/^\{/, "")
        .replace(/\}$/, "")
        .split(",")
        .map((tag) => tag.trim())
        .filter((tag) => tag.length > 0)
        .join(", ");
    },
    getTimezoneOffsetHours(timezoneValue) {
      const normalized = String(timezoneValue || "").trim().toUpperCase();
      const map = {
        PST: -8,
        PDT: -7,
        MST: -7,
        MDT: -6,
        CST: -6,
        CDT: -5,
        EST: -5,
        EDT: -4,
        UTC: 0,
      };
      return Object.prototype.hasOwnProperty.call(map, normalized) ? map[normalized] : 0;
    },
    buildScheduledEditDraft(clip) {
      if (!clip) return null;
      const metadata = this.normalizeClipMetadata(clip.clip_metadata);
      const timezone = this.getScheduledClipTimezoneLabel(clip);
      const { dateStr, timeStr } = this.getEventDatePartsByTimezone(clip.posting_at, timezone);
      return {
        title: String(clip.title || metadata.title || "").trim(),
        description: String(metadata.description || "").trim(),
        tags: String(metadata.hashtags || this.normalizeClipTagsText(clip.clip_tags)).trim(),
        date: dateStr,
        time: timeStr,
        timezone,
        metadata,
      };
    },
    buildScheduledMetadataPayload() {
      if (!this.manageScheduledEditDraft) return null;
      const draft = this.manageScheduledEditDraft;
      const metadata = this.normalizeClipMetadata(draft.metadata);
      const platforms = Array.isArray(metadata.platforms) ? metadata.platforms : [];
      const description = String(draft.description || "").trim();
      const hashtags = String(draft.tags || "").trim();
      const descriptionWithHashtags = this.composeDescriptionWithHashtags(description, hashtags);
      const rawPlatformMetadata =
        metadata.platform_metadata && typeof metadata.platform_metadata === "object" && !Array.isArray(metadata.platform_metadata)
          ? metadata.platform_metadata
          : {};
      const platformMetadata = {
        ...rawPlatformMetadata,
      };

      if (platforms.includes("instagram")) {
        platformMetadata.instagram = {
          ...(rawPlatformMetadata.instagram || {}),
          instagram_title: descriptionWithHashtags,
        };
      }
      if (platforms.includes("tiktok")) {
        platformMetadata.tiktok = {
          ...(rawPlatformMetadata.tiktok || {}),
          tiktok_title: descriptionWithHashtags,
        };
      }
      if (platforms.includes("youtube")) {
        platformMetadata.youtube = {
          ...(rawPlatformMetadata.youtube || {}),
          tags: this.parseCommaList(hashtags),
        };
      }
      if (platforms.includes("facebook")) {
        platformMetadata.facebook = {
          ...(rawPlatformMetadata.facebook || {}),
          facebook_description: descriptionWithHashtags,
        };
      }
      if (platforms.includes("x")) {
        platformMetadata.x = {
          ...(rawPlatformMetadata.x || rawPlatformMetadata.twitter || {}),
          x_title: descriptionWithHashtags,
        };
      }

      return {
        ...metadata,
        title: String(draft.title || "").trim(),
        description,
        hashtags,
        platform_metadata: platformMetadata,
      };
    },
    buildScheduledPostingIso() {
      const draft = this.manageScheduledEditDraft;
      if (!draft || !draft.date || !draft.time) return "";
      const dateParts = String(draft.date).split("-").map(Number);
      const timeParts = String(draft.time).split(":").map(Number);
      if (dateParts.length !== 3 || timeParts.length < 2) return "";
      if (dateParts.some((value) => Number.isNaN(value)) || timeParts.some((value) => Number.isNaN(value))) return "";
      const [year, month, day] = dateParts;
      const [hour, minute] = timeParts;
      const offsetHours = this.getTimezoneOffsetHours(draft.timezone);
      return new Date(Date.UTC(year, month - 1, day, hour - offsetHours, minute, 0)).toISOString();
    },
    canEditScheduledClip(clip) {
      return this.isAdminUser && this.canCancelScheduledUpload(clip);
    },
    canSaveScheduledEdit() {
      if (!this.manageScheduledEditDraft) return false;
      return Boolean(
        String(this.manageScheduledEditDraft.title || "").trim() &&
        this.manageScheduledEditDraft.date &&
        this.manageScheduledEditDraft.time
      );
    },
    extractSocialUrls(outputUrlsValue) {
      const outputUrls = this.normalizeOutputUrls(outputUrlsValue);
      if (!outputUrls) return [];

      const labelByPlatform = {
        instagram: "Instagram",
        tiktok: "TikTok",
        youtube: "YouTube",
        linkedin: "LinkedIn",
        facebook: "Facebook",
        x: "X",
        twitter: "Twitter",
      };

      const knownPlatforms = ["instagram", "tiktok", "youtube", "linkedin", "facebook", "x", "twitter"];
      const items = [];

      knownPlatforms.forEach((platform) => {
        const value = outputUrls[platform];
        if (typeof value === "string" && value.startsWith("http")) {
          items.push({
            platform,
            label: labelByPlatform[platform] || platform,
            url: this.normalizePlatformUrl(platform, value),
          });
        }
      });

      Object.keys(outputUrls).forEach((key) => {
        if (knownPlatforms.includes(key)) return;
        const value = outputUrls[key];
        if (typeof value === "string" && value.startsWith("http")) {
          items.push({
            platform: key,
            label: key.charAt(0).toUpperCase() + key.slice(1),
            url: this.normalizePlatformUrl(key, value),
          });
        }
      });

      return items;
    },
    normalizePlatformUrl(platform, url) {
      const raw = String(url || "").trim();
      if (!raw) return "";
      if (String(platform || "").toLowerCase() !== "youtube") return raw;
      try {
        const parsed = new URL(raw);
        const host = parsed.hostname.toLowerCase();
        const path = parsed.pathname || "";
        if (path.startsWith("/shorts/")) return raw;
        if (host.includes("youtube.com") && path === "/watch") {
          const videoId = parsed.searchParams.get("v");
          if (videoId) return `https://www.youtube.com/shorts/${videoId}`;
        }
        if (host.endsWith("youtu.be")) {
          const videoId = path.replace(/^\/+/, "").split("/")[0];
          if (videoId) return `https://www.youtube.com/shorts/${videoId}`;
        }
      } catch (error) {
        return raw;
      }
      return raw;
    },
    openManageScheduledModalFromPreview() {
      if (!this.previewClip || !this.previewClip.id) return;
      this.manageScheduledError = "";
      this.selectedScheduledClip = {
        id: this.previewClip.id,
        title: this.previewClip.title || this.selectedPreviewEvent?.title || null,
        status: this.previewClip.status || this.selectedPreviewEvent?.status || null,
        posting_at: this.previewClip.postingAt || this.selectedPreviewEvent?.posting_at || null,
        use_timezone:
          this.previewClip.userTimezone ||
          this.selectedPreviewEvent?.use_timezone ||
          "UTC",
        user_name:
          this.previewClip.user_name ||
          this.selectedPreviewEvent?.user_name ||
          null,
        clip_metadata: this.previewClip.clip_metadata || {},
        clip_tags: this.previewClip.clip_tags || [],
      };
      this.manageScheduledEditDraft = this.buildScheduledEditDraft(this.selectedScheduledClip);
      this.showManageScheduledModal = true;
    },
    closeManageScheduledModal(force = false) {
      if ((this.manageScheduledCancelling || this.manageScheduledSaving) && !force) return;
      this.showManageScheduledModal = false;
      this.selectedScheduledClip = null;
      this.manageScheduledError = "";
      this.manageScheduledEditDraft = null;
    },
    getScheduledClipTimezoneLabel(clip) {
      return (clip && clip.use_timezone) || "UTC";
    },
    getScheduledClipDateLabel(clip) {
      const postingAt = clip && clip.posting_at;
      if (!postingAt) return "-";
      const { dateStr } = this.getEventDatePartsByTimezone(
        postingAt,
        this.getScheduledClipTimezoneLabel(clip)
      );
      if (!dateStr) return "-";
      const parts = String(dateStr).split("-");
      if (parts.length !== 3) return dateStr;
      return `${parts[2]}/${parts[1]}/${parts[0]}`;
    },
    getScheduledClipTimeLabel(clip) {
      const postingAt = clip && clip.posting_at;
      if (!postingAt) return "-";
      const { timeStr } = this.getEventDatePartsByTimezone(
        postingAt,
        this.getScheduledClipTimezoneLabel(clip)
      );
      return timeStr || "-";
    },
    canCancelScheduledUpload(clip) {
      const status = this.normalizeClipStatus(clip && clip.status);
      return status === "5_scheduled_to_post";
    },
    async saveScheduledClipEdit() {
      const clip = this.selectedScheduledClip;
      if (!clip || !clip.id || !this.canEditScheduledClip(clip)) return;
      if (!this.canSaveScheduledEdit()) {
        this.manageScheduledError = this.tt("schedulePost.errorNoTitle", "El título es obligatorio.");
        return;
      }

      try {
        this.manageScheduledSaving = true;
        this.manageScheduledError = "";
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (!session) throw new Error("Sesion no valida");

        const clipMetadata = this.buildScheduledMetadataPayload();
        const postingAt = this.buildScheduledPostingIso();
        if (!postingAt) {
          throw new Error(this.tt("schedulePost.errorInvalidDate", "Fecha inválida"));
        }

        await updateTwitchClip(session, clip.id, {
          title: clipMetadata.title,
          status: "5_scheduled_to_post",
          posting_at: postingAt,
          clip_metadata: clipMetadata,
          clip_tags: this.parseCommaList(this.manageScheduledEditDraft.tags),
        });

        await this.loadTimeline();
        this.closeManageScheduledModal(true);
        this.closeEventPreview();
      } catch (error) {
        this.manageScheduledError = error?.message || this.tt("schedulePost.errorUpdateMetadata", "No se pudo actualizar la información del clip");
      } finally {
        this.manageScheduledSaving = false;
      }
    },
    async cancelScheduledUpload() {
      const clip = this.selectedScheduledClip;
      if (!clip || !clip.id) {
        this.manageScheduledError = "No hay clip seleccionado.";
        return;
      }

      try {
        this.manageScheduledCancelling = true;
        this.manageScheduledError = "";
        const apiUrl = process.env.VUE_APP_API_URL;
        if (!apiUrl) throw new Error("Falta VUE_APP_API_URL");
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (!session) throw new Error("Sesion no valida");

        const response = await fetch(`${apiUrl}/momo/twitch/clips/${encodeURIComponent(clip.id)}`, {
          method: "PATCH",
          headers: buildAuthHeaders(session),
          body: JSON.stringify({
            status: "4_rendered",
            posting_at: null,
          }),
        });
        if (!response.ok) {
          const errData = await response.json().catch(() => ({ detail: response.statusText }));
          throw new Error(errData.detail || "No se pudo cancelar la subida");
        }

        await this.loadTimeline();
        this.closeManageScheduledModal(true);
        this.closeEventPreview();
      } catch (error) {
        this.manageScheduledError = error?.message || "No se pudo cancelar la subida";
      } finally {
        this.manageScheduledCancelling = false;
      }
    },
  },
};
</script>

<style scoped>
.postadmin-timeline-page {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.timeline-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 20px;
  background: var(--clipr-bg-card);
  border-radius: 14px;
  border: 1px solid var(--clipr-border-color);
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.timeline-toolbar-title {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.timeline-main-title {
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 0.3px;
  color: var(--clipr-text-primary);
}

.timeline-toolbar-meta {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.timeline-month-label {
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--clipr-purple-light);
}

.timeline-stats {
  font-size: 12px;
  color: var(--clipr-text-tertiary);
}

.timeline-toolbar-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.timeline-status-summary {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.timeline-nav-btn {
  width: 38px;
  height: 38px;
  border: none;
  border-radius: 10px;
  background: rgba(168, 85, 247, 0.12);
  color: var(--clipr-purple-light);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
}

.timeline-nav-btn:hover {
  background: var(--clipr-purple-gradient);
  color: #fff;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px var(--clipr-purple-glow);
}

.timeline-user-filter {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px 16px;
  background: var(--clipr-bg-card);
  border: 1px solid var(--clipr-border-color);
  border-radius: 14px;
}

.timeline-user-filter-form {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.timeline-user-scope-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px;
  border: 1px solid var(--clipr-border-color);
  border-radius: 10px;
  background: rgba(15, 23, 42, 0.25);
}

.timeline-user-scope-btn {
  min-height: 30px;
  border: none;
  border-radius: 8px;
  padding: 0 10px;
  background: transparent;
  color: var(--clipr-text-secondary);
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
}

.timeline-user-scope-btn.active {
  background: rgba(168, 85, 247, 0.2);
  color: var(--clipr-purple-light);
}

.timeline-user-filter-input-wrap {
  min-width: min(320px, 100%);
  flex: 1;
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 12px;
  border: 1px solid var(--clipr-border-color);
  border-radius: 10px;
  background: rgba(15, 23, 42, 0.35);
  color: var(--clipr-text-tertiary);
}

.timeline-user-filter-input {
  width: 100%;
  min-height: 38px;
  border: none;
  outline: none;
  background: transparent;
  color: var(--clipr-text-primary);
  font-size: 13px;
}

.timeline-user-filter-input::placeholder {
  color: var(--clipr-text-tertiary);
}

.timeline-user-suggestions {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  z-index: 20;
  max-height: 260px;
  overflow-y: auto;
  padding: 6px;
  border: 1px solid var(--clipr-border-color);
  border-radius: 10px;
  background: var(--clipr-bg-card);
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.28);
}

.timeline-user-suggestion {
  width: 100%;
  border: none;
  border-radius: 8px;
  padding: 8px 10px;
  background: transparent;
  color: var(--clipr-text-primary);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  text-align: left;
  cursor: pointer;
}

.timeline-user-suggestion:hover {
  background: rgba(168, 85, 247, 0.14);
}

.timeline-user-suggestion-name {
  font-size: 13px;
  font-weight: 600;
}

.timeline-user-suggestion small {
  color: var(--clipr-text-tertiary);
  font-size: 11px;
}

.timeline-user-filter-add,
.timeline-user-filter-clear {
  min-height: 38px;
  border-radius: 10px;
  padding: 0 14px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
}

.timeline-user-filter-add {
  border: none;
  background: var(--clipr-purple-gradient);
  color: #fff;
}

.timeline-user-filter-add:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.timeline-user-filter-clear {
  border: 1px solid var(--clipr-border-color);
  background: transparent;
  color: var(--clipr-text-secondary);
}

.timeline-user-filter-chips {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.timeline-user-filter-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 8px 6px 10px;
  border-radius: 999px;
  border: 1px solid rgba(168, 85, 247, 0.35);
  background: rgba(168, 85, 247, 0.14);
  color: var(--clipr-purple-light);
  font-size: 12px;
  font-weight: 600;
}

.timeline-user-filter-chip button {
  width: 18px;
  height: 18px;
  border: none;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.12);
  color: inherit;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  cursor: pointer;
}

.timeline-sizer {
  width: 100%;
  height: 0;
}

.timeline-users {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.timeline-user-card {
  border: 1px solid var(--clipr-border-color);
  border-radius: 14px;
  padding: 18px;
  background: var(--clipr-bg-card);
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  transition: box-shadow 0.2s ease;
}

.timeline-user-card:hover {
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

.timeline-user-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--clipr-border-color);
}

.timeline-user-name-row {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.timeline-user-name {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: var(--clipr-text-primary);
}

.timeline-impersonate-btn {
  width: 32px;
  height: 32px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  border: none;
  background: rgba(168, 85, 247, 0.2);
  color: var(--clipr-purple-light);
  transition: all 0.2s ease;
}

.timeline-impersonate-btn:hover:not(:disabled) {
  background: var(--clipr-purple-gradient);
  color: #fff;
  transform: translateY(-1px);
}

.timeline-impersonate-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.timeline-assign-btn {
  min-height: 32px;
  padding: 0 10px;
  border-radius: 8px;
  border: 1px solid rgba(168, 85, 247, 0.35);
  background: rgba(168, 85, 247, 0.16);
  color: var(--clipr-purple-light);
  font-size: 12px;
  font-weight: 700;
  transition: all 0.2s ease;
}

.timeline-assign-btn:hover {
  background: var(--clipr-purple-gradient);
  color: #fff;
  transform: translateY(-1px);
}

.timeline-assign-btn.assigned {
  border-color: rgba(34, 197, 94, 0.45);
  background: rgba(22, 163, 74, 0.18);
  color: #86efac;
}

.timeline-assign-btn.assigned:hover {
  background: linear-gradient(135deg, #22c55e, #16a34a);
  color: #fff;
}

.timeline-assigned-admin-pill {
  display: inline-flex;
  align-items: center;
  max-width: 160px;
  border-radius: 999px;
  padding: 6px 10px;
  background: rgba(22, 163, 74, 0.14);
  border: 1px solid rgba(34, 197, 94, 0.35);
  color: #86efac;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.timeline-user-header small {
  display: block;
  margin-top: 2px;
  font-size: 11px;
  color: var(--clipr-text-tertiary);
}

.timeline-user-header .badge {
  font-size: 11px;
  font-weight: 600;
  padding: 6px 12px;
  border-radius: 999px;
  background: linear-gradient(135deg, rgba(168, 85, 247, 0.2), rgba(99, 102, 241, 0.15));
  border: 1px solid rgba(168, 85, 247, 0.35);
  color: var(--clipr-purple-light);
}

.timeline-user-status-summary {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.timeline-status-pill {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 5px 10px;
  font-size: 11px;
  font-weight: 600;
  border: 1px solid transparent;
}

.timeline-status-pill.ready {
  color: #c4b5fd;
  background: rgba(139, 92, 246, 0.16);
  border-color: rgba(139, 92, 246, 0.35);
}

.timeline-status-pill.posted {
  color: #86efac;
  background: rgba(22, 163, 74, 0.16);
  border-color: rgba(34, 197, 94, 0.35);
}

.timeline-days {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 12px;
}

.timeline-day-card {
  border: 1px solid var(--clipr-border-color);
  border-radius: 12px;
  padding: 12px;
  background: rgba(15, 23, 42, 0.4);
  min-height: 130px;
  display: flex;
  flex-direction: column;
  transition: all 0.2s ease;
}

.timeline-day-card.has-events {
  background: linear-gradient(135deg, rgba(15, 23, 42, 0.6), rgba(30, 64, 175, 0.25));
  border-color: rgba(168, 85, 247, 0.35);
}

.timeline-day-card.is-today {
  border-color: var(--clipr-purple-light);
  box-shadow: 0 0 0 1px rgba(168, 85, 247, 0.2);
}

.timeline-day-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
}

.timeline-day-name {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--clipr-text-tertiary);
}

.timeline-today-badge {
  display: inline-block;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  padding: 2px 6px;
  border-radius: 999px;
  margin: 4px 0 2px;
  align-self: flex-start;
  background: rgba(168, 85, 247, 0.25);
  color: var(--clipr-purple-light);
  border: 1px solid rgba(168, 85, 247, 0.45);
}

.timeline-day-number {
  font-size: 22px;
  font-weight: 700;
  margin-bottom: 10px;
  color: var(--clipr-text-primary);
}

.timeline-day-events {
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
  min-height: 0;
}

.timeline-day-event-pill {
  display: block;
  width: 100%;
  border-radius: 8px;
  padding: 6px 10px;
  font-size: 11px;
  font-weight: 500;
  background: var(--clipr-purple-gradient);
  color: #fff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  box-shadow: 0 2px 8px rgba(168, 85, 247, 0.3);
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.timeline-day-event-pill:hover {
  transform: translateX(2px);
  box-shadow: 0 2px 12px rgba(168, 85, 247, 0.4);
}

.timeline-day-empty {
  font-size: 11px;
  color: var(--clipr-text-tertiary);
  opacity: 0.8;
  font-style: italic;
}

.timeline-state {
  padding: 32px;
  color: var(--clipr-text-tertiary);
}

.timeline-preview-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(2, 6, 23, 0.72);
  backdrop-filter: blur(3px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
  padding: 16px;
}

.timeline-preview-modal {
  width: min(760px, 100%);
  max-height: calc(100vh - 32px);
  overflow: auto;
  border-radius: 14px;
  border: 1px solid var(--clipr-border-color);
  background: var(--clipr-bg-card);
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.45), 0 0 24px var(--clipr-purple-glow);
}

.timeline-assign-modal {
  width: min(560px, 100%);
  max-height: calc(100vh - 32px);
  overflow: auto;
  border-radius: 18px;
  border: 1px solid rgba(168, 85, 247, 0.28);
  background: var(--clipr-bg-card);
  box-shadow: 0 24px 70px rgba(0, 0, 0, 0.5), 0 0 32px var(--clipr-purple-glow);
}

.timeline-assign-modal-header {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 18px 20px;
  border-bottom: 1px solid var(--clipr-border-color);
  background:
    radial-gradient(circle at top left, rgba(168, 85, 247, 0.24), transparent 36%),
    rgba(15, 23, 42, 0.28);
}

.timeline-assign-header-icon {
  width: 42px;
  height: 42px;
  border-radius: 14px;
  background: var(--clipr-purple-gradient);
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 22px var(--clipr-purple-glow);
}

.timeline-assign-header-copy {
  flex: 1;
  min-width: 0;
}

.timeline-assign-modal-body {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.timeline-assign-target-card {
  border: 1px solid rgba(168, 85, 247, 0.28);
  border-radius: 14px;
  padding: 12px 14px;
  background: linear-gradient(135deg, rgba(168, 85, 247, 0.16), rgba(99, 102, 241, 0.08));
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.timeline-assign-target-label {
  color: var(--clipr-text-tertiary);
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.timeline-assign-target-card strong {
  color: var(--clipr-text-primary);
  font-size: 15px;
}

.timeline-assign-target-card small {
  color: var(--clipr-text-secondary);
  font-size: 12px;
}

.timeline-preview-modal-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 18px;
  border-bottom: 1px solid var(--clipr-border-color);
}

.timeline-preview-modal-title {
  color: var(--clipr-text-primary);
  font-weight: 700;
  font-size: 16px;
}

.timeline-preview-modal-subtitle {
  color: var(--clipr-text-secondary);
  font-size: 13px;
}

.timeline-preview-close {
  width: 32px;
  height: 32px;
  border: 1px solid var(--clipr-border-color);
  background: transparent;
  border-radius: 8px;
  color: var(--clipr-text-secondary);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.timeline-preview-close:hover {
  color: var(--clipr-text-primary);
  border-color: var(--clipr-border-purple);
}

.timeline-preview-modal-body {
  padding: 16px 18px 18px;
}

.timeline-preview-content {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.timeline-preview-video {
  width: min(360px, 100%);
  aspect-ratio: 9 / 16;
  max-height: 72vh;
  border-radius: 10px;
  border: 1px solid var(--clipr-border-color);
  background: #000;
  display: block;
  margin: 0 auto;
  object-fit: cover;
}

.timeline-preview-no-video {
  border: 1px dashed var(--clipr-border-color);
  border-radius: 10px;
  min-height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: var(--clipr-text-secondary);
  background: rgba(15, 23, 42, 0.35);
}

.timeline-preview-meta {
  background: rgba(15, 23, 42, 0.35);
  border: 1px solid var(--clipr-border-color);
  border-radius: 10px;
  padding: 12px 14px;
  color: var(--clipr-text-secondary);
  font-size: 13px;
}

.timeline-preview-social-urls {
  margin-bottom: 8px;
}

.timeline-preview-social-list {
  margin-top: 6px;
  padding-left: 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.timeline-preview-social-item a {
  color: #93c5fd;
  text-decoration: underline;
  word-break: break-all;
}

.timeline-preview-social-item a:hover {
  color: #bfdbfe;
}

.timeline-preview-social-label {
  color: var(--clipr-text-primary);
  margin-right: 4px;
  font-weight: 600;
}

.timeline-preview-actions {
  display: flex;
  justify-content: flex-end;
}

.timeline-admin-btn {
  border: none;
  border-radius: 8px;
  padding: 8px 12px;
  background: var(--clipr-purple-gradient);
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}

.timeline-admin-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.timeline-admin-picker {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.timeline-admin-search-wrap {
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 12px;
  border: 1px solid var(--clipr-border-color);
  border-radius: 12px;
  background: rgba(15, 23, 42, 0.35);
  color: var(--clipr-text-tertiary);
}

.timeline-admin-search-input {
  width: 100%;
  min-height: 40px;
  border: none;
  outline: none;
  background: transparent;
  color: var(--clipr-text-primary);
  font-size: 13px;
}

.timeline-admin-search-input::placeholder {
  color: var(--clipr-text-tertiary);
}

.timeline-admin-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: min(340px, 45vh);
  overflow-y: auto;
  padding-right: 2px;
}

.timeline-admin-option {
  width: 100%;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 14px;
  padding: 10px;
  background: rgba(15, 23, 42, 0.35);
  color: var(--clipr-text-primary);
  display: flex;
  align-items: center;
  gap: 10px;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.18s ease, background 0.18s ease, transform 0.18s ease;
}

.timeline-admin-option:hover:not(:disabled),
.timeline-admin-option.selected {
  border-color: var(--clipr-border-purple);
  background: rgba(168, 85, 247, 0.14);
}

.timeline-admin-option:hover:not(:disabled) {
  transform: translateY(-1px);
}

.timeline-admin-option-avatar {
  width: 36px;
  height: 36px;
  border-radius: 12px;
  background: rgba(168, 85, 247, 0.2);
  color: var(--clipr-purple-light);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 800;
  flex: 0 0 auto;
}

.timeline-admin-option.selected .timeline-admin-option-avatar {
  background: var(--clipr-purple-gradient);
  color: #fff;
}

.timeline-admin-option-copy {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
}

.timeline-admin-option-copy strong {
  color: var(--clipr-text-primary);
  font-size: 13px;
}

.timeline-admin-option-copy small {
  color: var(--clipr-text-tertiary);
  font-size: 11px;
}

.timeline-admin-option-check {
  width: 24px;
  height: 24px;
  border-radius: 999px;
  border: 1px solid var(--clipr-border-color);
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
}

.timeline-admin-option.selected .timeline-admin-option-check {
  border-color: rgba(168, 85, 247, 0.65);
  background: var(--clipr-purple-gradient);
}

.timeline-assign-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding-top: 4px;
}

.timeline-manage-modal {
  width: min(760px, 100%);
  max-height: calc(100vh - 32px);
  overflow: auto;
  border-radius: 14px;
  border: 1px solid var(--clipr-border-color);
  background: var(--clipr-bg-card);
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.45), 0 0 24px var(--clipr-purple-glow);
}

.scheduled-clip-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
  margin-top: 12px;
}

.scheduled-clip-item {
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 10px;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.scheduled-clip-label {
  font-size: 12px;
  color: var(--clipr-text-secondary);
}

.scheduled-clip-value {
  font-size: 13px;
  color: var(--clipr-text-primary);
  word-break: break-word;
}

.timeline-scheduled-editor {
  margin-top: 14px;
  border: 1px solid rgba(168, 85, 247, 0.25);
  border-radius: 12px;
  padding: 14px;
  background: rgba(15, 23, 42, 0.35);
}

.timeline-scheduled-editor-header {
  display: flex;
  flex-direction: column;
  gap: 3px;
  margin-bottom: 12px;
}

.timeline-scheduled-editor-header strong {
  color: var(--clipr-text-primary);
  font-size: 13px;
}

.timeline-scheduled-editor-header small {
  color: var(--clipr-text-tertiary);
  font-size: 12px;
}

.timeline-scheduled-editor-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
}

.timeline-scheduled-editor-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin: 0;
}

.timeline-scheduled-editor-field--full {
  grid-column: 1 / -1;
}

.timeline-scheduled-editor-field span {
  color: var(--clipr-text-secondary);
  font-size: 12px;
  font-weight: 600;
}

.timeline-scheduled-editor-input {
  width: 100%;
  min-height: 38px;
  border: 1px solid var(--clipr-border-color);
  border-radius: 9px;
  padding: 8px 10px;
  background: rgba(15, 23, 42, 0.62);
  color: var(--clipr-text-primary);
  font-size: 13px;
}

.timeline-scheduled-editor-textarea {
  min-height: 78px;
  resize: vertical;
}

.timeline-scheduled-editor-timezone {
  min-height: 38px;
  display: inline-flex;
  align-items: center;
  border: 1px solid var(--clipr-border-color);
  border-radius: 9px;
  padding: 8px 10px;
  background: rgba(255, 255, 255, 0.04);
  color: var(--clipr-text-primary);
  font-size: 13px;
}

.timeline-manage-actions {
  margin-top: 14px;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  flex-wrap: wrap;
}

.timeline-cancel-upload-btn {
  border: none;
  border-radius: 8px;
  padding: 9px 12px;
  background: linear-gradient(135deg, #ef4444, #dc2626);
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
}

.timeline-manage-close-btn {
  border: 1px solid var(--clipr-border-color);
  border-radius: 8px;
  padding: 9px 12px;
  background: transparent;
  color: var(--clipr-text-primary);
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}

@media (max-width: 640px) {
  .timeline-scheduled-editor-grid {
    grid-template-columns: 1fr;
  }
}

.rotating {
  animation: spin 1.1s linear infinite;
  font-size: 24px;
  color: var(--clipr-purple-light);
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
